import {
    ActionIcon,
    Alert,
    Anchor,
    Button,
    Group,
    Menu,
    Modal,
    Stack,
    Text,
    Textarea,
    Title,
} from '@mantine/core'
import {IconChevronDown, IconDotsVertical, IconEdit, IconMessageShare} from '@tabler/icons-react'
import {Link, useNavigate} from '@tanstack/react-router'
import {type ReactNode, useEffect, useState} from 'react'

import {AutoWidthTitleInput} from 'src/ui/auto-width-title-input'
import {DetailPageBackLink} from 'src/ui/detail-page-back-link'

import {mintDriverLinks} from '../driver-view/api'
import {DriverLinksDialog} from '../driver-view/driver-links-dialog'
import type {DriverLink} from '../driver-view/schemas'
import {seedDuplicateJobDraft} from './create-job-storage'
import {useEditRights} from './edit-rights-context'
import type {JobDataLayer} from './job-data-layer'
import {
    didNotHappenNextStep,
    getJobStatusModalContent,
    type JobLifecycleAction,
} from './job-details-utils'
import {JobStatusBadge, jobStatusColor, jobStatusLabel} from './job-status-badge'
import type {JobDetail} from './types'

type JobDetailsPageTitleProps = {
    job: JobDetail
    projectName: string | null
    jobDataLayer: JobDataLayer
    onJobUpdated: (job: JobDetail) => void
    canEdit?: boolean
    actions?: ReactNode
}

function workLineDetails(job: Pick<JobDetail, 'projectJobCount' | 'isPickup'>): string {
    const parts = []
    if (job.projectJobCount) {
        parts.push(`${job.projectJobCount} ${job.projectJobCount === 1 ? 'job' : 'jobs'}`)
    }
    if (job.isPickup) {
        parts.push('Pickup')
    }
    return parts.map((part) => ` · ${part}`).join('')
}

export function JobDetailsPageTitle({
    job,
    projectName,
    jobDataLayer,
    onJobUpdated,
    canEdit = true,
    actions,
}: JobDetailsPageTitleProps) {
    const navigate = useNavigate()
    const {canDispatch} = useEditRights()
    const [isEditing, setIsEditing] = useState(false)
    const [jobName, setJobName] = useState(job.name)
    const [saving, setSaving] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [driverLinks, setDriverLinks] = useState<DriverLink[] | null>(null)
    const [statusModalOpen, setStatusModalOpen] = useState(false)
    const [lifecycleSaving, setLifecycleSaving] = useState(false)
    const [sharingDriverLink, setSharingDriverLink] = useState(false)
    const [cancelReason, setCancelReason] = useState<string | null>(null)

    const statusModalContent = getJobStatusModalContent(job.status)
    const statusLabel = jobStatusLabel(job.status)

    useEffect(() => {
        if (!isEditing) {
            setJobName(job.name)
        }
    }, [job.name, isEditing])

    function startEditing() {
        setJobName(job.name)
        setErrorMessage(null)
        setIsEditing(true)
    }

    function handleCancel() {
        setJobName(job.name)
        setErrorMessage(null)
        setIsEditing(false)
    }

    async function handleSave() {
        const trimmedName = jobName.trim()
        if (!trimmedName) {
            setErrorMessage('Job name is required')
            return
        }

        setSaving(true)
        setErrorMessage(null)
        try {
            const updatedJob = await jobDataLayer.patchJob(job.id, {
                jobName: trimmedName,
                serviceTypeId: job.serviceTypeId,
                notes: job.notes,
                customerId: job.customerId,
            })
            onJobUpdated(updatedJob)
            setIsEditing(false)
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save job name')
        } finally {
            setSaving(false)
        }
    }

    async function handleConfirmLifecycleAction(action: JobLifecycleAction) {
        setLifecycleSaving(true)
        setErrorMessage(null)
        try {
            let updatedJob: JobDetail
            switch (action) {
                case 'approve':
                    updatedJob = await jobDataLayer.approveJob(job.id)
                    break
                case 'archive':
                    updatedJob = await jobDataLayer.archiveJob(job.id)
                    break
                case 'cancel':
                    if (cancelReason == null) {
                        setCancelReason('')
                        return
                    }
                    if (!cancelReason.trim()) {
                        return
                    }

                    updatedJob = await jobDataLayer.cancelJob(job.id, cancelReason.trim())
                    break
                case 'reopen':
                    updatedJob = await jobDataLayer.reopenJob(
                        job.id,
                        job.status === 'closed' && job.approvedToStart,
                    )
                    break
            }
            onJobUpdated(updatedJob)
            setStatusModalOpen(false)
            setCancelReason(null)
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to update job status')
        } finally {
            setLifecycleSaving(false)
        }
    }

    function handleDuplicateJob() {
        seedDuplicateJobDraft({
            id: job.id,
            customerId: job.customerId,
            customerName: job.customerName,
        })
        void navigate({to: '/jobs/create'})
    }

    async function handleShareDriverLink() {
        setSharingDriverLink(true)
        setErrorMessage(null)
        try {
            setDriverLinks(await mintDriverLinks(job.id))
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to get driver links')
        } finally {
            setSharingDriverLink(false)
        }
    }

    return (
        <Stack gap={4}>
            <DetailPageBackLink to="/jobs" listLabel="jobs" />

            <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
                <Group gap="sm" align="center" wrap="nowrap" style={{minWidth: 0, flex: 1}}>
                    {isEditing ? (
                        <AutoWidthTitleInput
                            value={jobName}
                            onChange={(event) => setJobName(event.currentTarget.value)}
                            ariaLabel="Job name"
                        />
                    ) : (
                        <Title order={1} style={{minWidth: 0}}>
                            {job.displayId}: {job.name}
                        </Title>
                    )}

                    {!canEdit ? null : !isEditing ? (
                        <ActionIcon
                            variant="subtle"
                            size="lg"
                            aria-label="Edit job name"
                            onClick={startEditing}
                        >
                            <IconEdit size={20} stroke={1.5} />
                        </ActionIcon>
                    ) : (
                        <Group gap="xs" wrap="nowrap">
                            <Button
                                variant="default"
                                size="compact-sm"
                                onClick={handleCancel}
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="compact-sm"
                                onClick={() => void handleSave()}
                                loading={saving}
                            >
                                Save
                            </Button>
                        </Group>
                    )}
                </Group>

                <Group gap="sm" wrap="nowrap" align="center">
                    {canEdit ? (
                        <Button
                            variant="light"
                            color={jobStatusColor(job.status)}
                            size="compact-lg"
                            rightSection={<IconChevronDown size={14} stroke={1.5} />}
                            aria-label={`Job status: ${statusLabel}. View status details and next steps.`}
                            onClick={() => setStatusModalOpen(true)}
                        >
                            {statusLabel}
                        </Button>
                    ) : (
                        <JobStatusBadge status={job.status} variant="light" />
                    )}
                    {job.status === 'in_progress' && canDispatch ? (
                        <Button
                            variant="default"
                            size="compact-lg"
                            leftSection={<IconMessageShare size={20} stroke={1.5} />}
                            loading={sharingDriverLink}
                            onClick={() => void handleShareDriverLink()}
                        >
                            Share with driver
                        </Button>
                    ) : null}
                    <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                            <ActionIcon variant="default" size="lg" aria-label="Job actions">
                                <IconDotsVertical size={18} stroke={1.5} />
                            </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Item onClick={handleDuplicateJob}>Duplicate this job</Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                </Group>
            </Group>

            {errorMessage && (
                <Text size="sm" c="red">
                    {errorMessage}
                </Text>
            )}

            <DriverLinksDialog links={driverLinks} onClose={() => setDriverLinks(null)} />

            {job.status === 'canceled' && job.cancelReason ? (
                <Text size="sm" data-testid="job-cancel-reason">
                    <Text span fw={600}>
                        Canceled:
                    </Text>{' '}
                    <Text span style={{whiteSpace: 'pre-wrap'}}>
                        {job.cancelReason}
                    </Text>
                </Text>
            ) : null}

            {job.didNotHappenAt && job.status !== 'canceled' ? (
                <Alert color="yellow" variant="light" data-testid="job-did-not-happen">
                    Reported as did not happen
                    {job.didNotHappenByName ? ` by ${job.didNotHappenByName}` : ''}.{' '}
                    {didNotHappenNextStep(job.status)}
                </Alert>
            ) : null}

            <Group justify="space-between" align="center" gap="sm" wrap="wrap">
                <Text size="sm" c="dimmed" data-testid="job-work-line">
                    {job.orderNumber ? `Part of order ${job.orderNumber}:` : 'Part of order:'}{' '}
                    <Anchor
                        component={Link}
                        to="/projects/$projectId"
                        params={{projectId: job.projectId}}
                        size="sm"
                    >
                        {projectName ?? job.projectName ?? 'View order'}
                    </Anchor>
                    {workLineDetails(job)}
                </Text>
                {actions ? (
                    <Group gap="xs" wrap="wrap">
                        {actions}
                    </Group>
                ) : null}
            </Group>

            <Modal
                opened={statusModalOpen}
                onClose={() => {
                    setStatusModalOpen(false)
                    setCancelReason(null)
                }}
                title={
                    <Group gap="xs" wrap="nowrap">
                        <Text span>Job status:</Text>
                        <JobStatusBadge status={job.status} variant="light" />
                    </Group>
                }
                centered
            >
                <Stack gap="md">
                    <Stack gap={4}>
                        <Text size="sm" fw={600}>
                            Current
                        </Text>
                        <Text size="sm">{statusModalContent.currentDescription}</Text>
                    </Stack>
                    {cancelReason != null ? (
                        <Textarea
                            label="Cancel reason"
                            placeholder="e.g. Customer request"
                            withAsterisk
                            autosize
                            minRows={2}
                            value={cancelReason}
                            onChange={(event) => setCancelReason(event.currentTarget.value)}
                            data-autofocus
                        />
                    ) : null}
                    {statusModalContent.nextDescription ? (
                        <Stack gap={4}>
                            <Text size="sm" fw={600}>
                                Next step
                            </Text>
                            <Text size="sm">{statusModalContent.nextDescription}</Text>
                        </Stack>
                    ) : null}
                    <Group justify="space-between" align="center">
                        <Group gap="sm">
                            {statusModalContent.actions
                                .filter((action) => action.destructive)
                                .map((action) => (
                                    <Button
                                        key={action.lifecycleAction}
                                        variant="filled"
                                        color="red"
                                        loading={lifecycleSaving}
                                        disabled={
                                            action.lifecycleAction === 'cancel' &&
                                            cancelReason != null &&
                                            !cancelReason.trim()
                                        }
                                        onClick={() =>
                                            void handleConfirmLifecycleAction(
                                                action.lifecycleAction,
                                            )
                                        }
                                    >
                                        {action.label}
                                    </Button>
                                ))}
                        </Group>
                        <Group gap="sm">
                            {statusModalContent.actions
                                .filter((action) => !action.destructive)
                                .map((action) => (
                                    <Button
                                        key={action.lifecycleAction}
                                        variant={action.variant ?? 'filled'}
                                        loading={lifecycleSaving}
                                        onClick={() =>
                                            void handleConfirmLifecycleAction(
                                                action.lifecycleAction,
                                            )
                                        }
                                    >
                                        {action.label}
                                    </Button>
                                ))}
                        </Group>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    )
}
